import {
  useImpersonateContext,
  useRoundAdmin,
} from "@client/ImpersonateContext";
import { isRoundArchived } from "@engine/round/accessors";
import { ArchiveIcon } from "lucide-react";

/**
 * Round-scoped banner shown across every round route while the round is
 * archived. The engine refuses every action on an archived round, so this is
 * the one place that tells the viewer why their edits would be rejected and,
 * for round admins, where to undo it. Reads the round through the header's
 * impersonation context like the rest of the header chrome, so it re-renders
 * with the shell rather than subscribing to round state on its own.
 */
export function ArchivedRoundBanner() {
  const { value } = useImpersonateContext();
  const { isAdmin } = useRoundAdmin();
  const round = value?.round;
  if (!round || !isRoundArchived(round)) return null;

  const settingsHref = `/round/${round.id}${
    round.isSfcRound ? "/admin/round-settings" : "/settings/round"
  }`;

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 border-b border-amber-500/40 bg-amber-500/15 px-4 py-1.5 text-sm"
    >
      <ArchiveIcon className="size-4 shrink-0 text-amber-700 dark:text-amber-400" />
      <span>
        <strong className="font-semibold">This round is archived.</strong> It is
        read-only: you can browse everything, but evaluations, notes, and
        settings can&apos;t be changed.
        {isAdmin && (
          <>
            {" "}
            <a href={settingsHref} className="font-medium underline">
              Restore it in Round Settings
            </a>
            .
          </>
        )}
      </span>
    </div>
  );
}
